/**
 * Shell（親）と microapp の client.js（sandbox iframe）の間で使うMessageChannel契約。
 * api-contract.md 4章の封筒形式・上限・operation一覧を正本とする。
 *
 * この1ファイルに封筒・上限・operationごとの入力検証をまとめている。
 * 親と子の両方が同じ定義を読み、片側だけが緩い検証をする状態を作らないため。
 */

export const PROTOCOL = 'microapp.v1' as const;

export const LIMITS = {
  /** 1メッセージのJSON上限。 */
  maxMessageBytes: 1024 * 1024,
  /** 同時に処理中にできる要求数。 */
  maxConcurrentRequests: 32,
  /** 1frameが保持できる購読数。 */
  maxSubscriptions: 4,
  /** rpc の待ち時間。 */
  rpcTimeoutMs: 10_000,
  /** rpc 以外のHTTP受付の待ち時間。timeoutは業務処理の取消を意味しない。 */
  httpTimeoutMs: 30_000,
} as const;

export const OPERATIONS = [
  'rpc',
  'config.get',
  'tasks.get',
  'tasks.answer',
  'runs.start',
  'runs.get',
  'runs.diagram',
  'assets.get',
  'subscribe',
  'unsubscribe',
] as const;

export type Operation = (typeof OPERATIONS)[number];

/** rpc だけが5秒契約のread処理。それ以外はHTTPの受付時間を使う。 */
export function timeoutFor(operation: Operation): number {
  return operation === 'rpc' ? LIMITS.rpcTimeoutMs : LIMITS.httpTimeoutMs;
}

// ---- 封筒 -----------------------------------------------------------------

/**
 * frame → Shell。bootstrapが1回だけ送る。
 *
 * nonceはShellがそのsrcdocへ埋めた使い捨ての値。
 * 同じiframe要素へsrcdocを入れ直すと、置き換えられる前の文書のreadyが後から届くことがある
 * （WindowProxyはnavigationを跨いで同一なのでsourceでは区別できない）。
 * 古い文書へportを渡さないため、Shellは自分が今埋めたnonceだけを受け付ける。
 */
export interface ReadyEnvelope {
  protocol: typeof PROTOCOL;
  type: 'ready';
  nonce: string;
}

/** Shell → frame。MessagePortをtransferする唯一のwindow message。 */
export interface PortEnvelope {
  protocol: typeof PROTOCOL;
  type: 'port';
}

export function isPortMessage(raw: unknown): raw is PortEnvelope {
  return isRecord(raw) && raw.protocol === PROTOCOL && raw.type === 'port';
}

export interface RequestEnvelope {
  protocol: typeof PROTOCOL;
  type: 'request';
  id: string;
  operation: Operation;
  payload: Record<string, unknown>;
}

export interface ResponseOk {
  protocol: typeof PROTOCOL;
  type: 'response';
  id: string;
  ok: true;
  value: unknown;
}

export interface ResponseError {
  protocol: typeof PROTOCOL;
  type: 'response';
  id: string;
  ok: false;
  error: ProtocolError;
}

export type ResponseEnvelope = ResponseOk | ResponseError;

export interface EventEnvelope {
  protocol: typeof PROTOCOL;
  type: 'event';
  subscriptionId: string;
  event: GuestEvent;
}

/** iframeへ渡すイベント。状態と識別子だけで、入力・出力・回答本文を含めない。 */
export interface GuestEvent {
  id: string;
  type: string;
  runId: string | null;
  requestId: string | null;
  status: string;
  cursor: string;
  occurredAt: string;
}

export type HostMessage = ResponseEnvelope | EventEnvelope;

/**
 * frame → Shell（port経由）。信頼bootstrapがpagehideで送る。
 *
 * frameが自分で別文書へ移ったことを、loadイベントの回数からは判別できない
 * （Shellがsrcdocを入れる過程でもloadは複数回起きる）。
 * 先に走るbootstrapだけが送れるこの合図を、遷移の判定に使う。
 */
export interface UnloadingEnvelope {
  protocol: typeof PROTOCOL;
  type: 'unloading';
}

export function isUnloadingMessage(raw: unknown): raw is UnloadingEnvelope {
  return isRecord(raw) && raw.protocol === PROTOCOL && raw.type === 'unloading';
}

export interface ProtocolError {
  code: ErrorCode | string;
  message: string;
  traceId: string;
}

/** Shellが自分で返す拒否理由。APIが返したproblemのcodeはそのまま透過させる。 */
export type ErrorCode =
  | 'invalid_request'
  | 'unknown_operation'
  | 'payload_too_large'
  | 'too_many_requests'
  | 'too_many_subscriptions'
  | 'unknown_subscription'
  | 'forbidden_resource'
  | 'timeout'
  | 'frame_replaced'
  | 'upstream_error';

// ---- 検証 -----------------------------------------------------------------

export type Validation<T> = { ok: true; value: T } | { ok: false; error: ProtocolError };

function reject(code: ErrorCode, message: string): { ok: false; error: ProtocolError } {
  return { ok: false, error: { code, message, traceId: '' } };
}

export function isReadyMessage(raw: unknown): raw is ReadyEnvelope {
  return (
    isRecord(raw) &&
    raw.protocol === PROTOCOL &&
    raw.type === 'ready' &&
    typeof raw.nonce === 'string' &&
    raw.nonce.length > 0
  );
}

/**
 * client.js から届いた要求の封筒を検証する。
 * 未知operation・未知field・過大payloadはここで落とし、API呼び出しへ進めない。
 */
export function parseRequest(raw: unknown): Validation<RequestEnvelope> {
  if (!isRecord(raw)) return reject('invalid_request', 'messageがobjectではありません');
  if (raw.protocol !== PROTOCOL) return reject('invalid_request', 'protocolが一致しません');
  if (raw.type !== 'request') return reject('invalid_request', 'typeがrequestではありません');
  if (typeof raw.id !== 'string' || raw.id.length === 0 || raw.id.length > 128) {
    return reject('invalid_request', 'idが不正です');
  }
  if (typeof raw.operation !== 'string' || !isOperation(raw.operation)) {
    return reject('unknown_operation', `未知のoperationです: ${String(raw.operation)}`);
  }
  if (!isRecord(raw.payload)) return reject('invalid_request', 'payloadがobjectではありません');

  const extra = Object.keys(raw).filter(
    (key) => !['protocol', 'type', 'id', 'operation', 'payload'].includes(key),
  );
  if (extra.length > 0) return reject('invalid_request', `未知のfieldがあります: ${extra.join(',')}`);

  const payload = validatePayload(raw.operation, raw.payload);
  if (!payload.ok) return payload;

  return {
    ok: true,
    value: {
      protocol: PROTOCOL,
      type: 'request',
      id: raw.id,
      operation: raw.operation,
      payload: payload.value,
    },
  };
}

export function isOperation(value: string): value is Operation {
  return (OPERATIONS as readonly string[]).includes(value);
}

/**
 * メッセージの大きさ。文字数ではなくUTF-8のbyte数で判定する。
 * 構造化クローンはJSONにできない値（循環参照・BigInt・関数を持つオブジェクト等）も運べるため、
 * JSON化できない場合はnullを返し、呼び出し側が「契約外の入力」として扱えるようにする。
 */
export function jsonByteLength(value: unknown): number | null {
  try {
    const json = JSON.stringify(value);
    if (json === undefined) return null;
    return new TextEncoder().encode(json).length;
  } catch {
    return null;
  }
}

/** 上限超過とJSON化不能をまとめて「受け付けない」と判定する。 */
export function exceedsMessageLimit(value: unknown): boolean {
  const length = jsonByteLength(value);
  return length === null || length > LIMITS.maxMessageBytes;
}

// ---- operationごとの入力 ---------------------------------------------------

/**
 * operationごとの入力契約はこの検証だけが持ち、payloadの形をinterfaceでは宣言しない。
 * 同じ形を型と検証の二箇所に書くと、片方だけ直された状態を作れてしまうため。
 */
function validatePayload(
  operation: Operation,
  payload: Record<string, unknown>,
): Validation<Record<string, unknown>> {
  switch (operation) {
    case 'rpc':
      return shape(payload, {
        method: nonEmptyString(200),
        input: jsonObject,
      });
    case 'config.get':
      return shape(payload, {});
    case 'tasks.get':
      return shape(payload, { requestId: nonEmptyString(200) });
    case 'tasks.answer':
      return shape(payload, {
        requestId: nonEmptyString(200),
        answer: jsonObject,
        expectedVersion: positiveInteger,
        idempotencyKey: nonEmptyString(200),
      });
    case 'runs.start':
      return shape(payload, {
        processKey: nonEmptyString(200),
        input: jsonObject,
        idempotencyKey: nonEmptyString(200),
      });
    case 'runs.get':
    case 'runs.diagram':
      return shape(payload, { runId: nonEmptyString(200) });
    case 'assets.get':
      return shape(payload, { assetId: nonEmptyString(200) });
    case 'subscribe':
      return shape(payload, {
        subscriptionId: nonEmptyString(64),
        filter: subscribeFilter,
      });
    case 'unsubscribe':
      return shape(payload, { subscriptionId: nonEmptyString(64) });
  }
}

type FieldCheck = (value: unknown) => string | null;

function shape(
  payload: Record<string, unknown>,
  fields: Record<string, FieldCheck>,
): Validation<Record<string, unknown>> {
  const known = Object.keys(fields);
  const extra = Object.keys(payload).filter((key) => !known.includes(key));
  if (extra.length > 0) {
    return reject('invalid_request', `payloadに未知のfieldがあります: ${extra.join(',')}`);
  }
  for (const [key, check] of Object.entries(fields)) {
    const problem = check(payload[key]);
    if (problem) return reject('invalid_request', `${key}: ${problem}`);
  }
  return { ok: true, value: payload };
}

function nonEmptyString(maxLength: number): FieldCheck {
  return (value) => {
    if (typeof value !== 'string') return '文字列が必要です';
    if (value.length === 0) return '空文字は使えません';
    if (value.length > maxLength) return `${maxLength}文字以内が必要です`;
    return null;
  };
}

const positiveInteger: FieldCheck = (value) =>
  Number.isInteger(value) && (value as number) >= 1 ? null : '1以上の整数が必要です';

const jsonObject: FieldCheck = (value) =>
  isRecord(value) ? null : 'JSON objectが必要です';

/** appIdはframeに固定した値だけを使うので、filterでは指定させない（他appの購読を防ぐ）。 */
const subscribeFilter: FieldCheck = (value) => {
  if (!isRecord(value)) return 'JSON objectが必要です';
  const extra = Object.keys(value).filter((key) => !['runId', 'requestId'].includes(key));
  if (extra.length > 0) return `未知のfieldがあります: ${extra.join(',')}`;
  for (const key of ['runId', 'requestId'] as const) {
    const entry = value[key];
    if (entry === undefined) continue;
    if (typeof entry !== 'string' || entry.length === 0 || entry.length > 200) {
      return `${key}が不正です`;
    }
  }
  return null;
};

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
